import React, { useState } from 'react';
import MainLayout from '../components/layout/MainLayout';
import { toast } from 'react-toastify';

const EducationResources = () => {
  const [healthLiteracy, setHealthLiteracy] = useState(50); // 0-100 scale
  const [language, setLanguage] = useState('English');
  const [visualStyle, setVisualStyle] = useState('Illustration');
  const [resources, setResources] = useState({
    sideEffects: true,
    timeline: true,
    statistics: true,
    questions: true
  });
  const [visualStyleMenuOpen, setVisualStyleMenuOpen] = useState(false);

  // Handle settings changes
  const handleResourceToggle = (resource) => {
    setResources({
      ...resources,
      [resource]: !resources[resource]
    });
    toast.info(`${resource} ${!resources[resource] ? 'added to' : 'removed from'} resources`);
  };

  // Handle share actions
  const handleShare = (method) => {
    switch(method) {
      case 'email':
        toast.success('Education material sent via email to patient');
        break;
      case 'print':
        toast.info('Preparing document for printing...');
        setTimeout(() => {
          toast.success('Document ready for printing');
          // In a real app, this would open a print dialog
          window.print();
        }, 1000);
        break;
      case 'portal':
        toast.success('Education material shared to patient portal');
        break;
      default:
        break;
    }
  };

  const handleCustomize = () => {
    toast.info('Opening customization editor...');
    // This would open a modal or navigate to an editor in a real implementation
  };

  const handleVisualStyleChange = (style) => {
    setVisualStyle(style);
    setVisualStyleMenuOpen(false);
    toast.info(`Visual style changed to ${style}`);
  };

  // Illustration mode (default) content
  const IllustrationContent = () => (
    <div className="relative">
      {/* Diagnosis Box */}
      <div className="bg-blue-500 text-white p-3 rounded text-center mb-4 w-full">
        Diagnosis
      </div>
      
      {/* Vertical connector line */}
      <div className="absolute left-1/2 transform -translate-x-1/2" style={{top: '3rem', height: '2rem', width: '2px', backgroundColor: '#718096'}}></div>
      
      {/* Staging Box */}
      <div className="bg-blue-500 text-white p-3 rounded text-center mb-4 w-full">
        Staging
      </div>
      
      {/* Connecting lines for Treatment Options */}
      <div className="flex items-center justify-center mb-4 relative">
        {/* Horizontal line */}
        <div className="h-0.5 bg-gray-400 w-full absolute top-10"></div>
        
        {/* Vertical lines */}
        <div className="flex justify-between w-full">
          <div className="w-5/12 relative">
            <div className="absolute left-1/2 transform -translate-x-1/2" style={{top: '0', height: '2rem', width: '2px', backgroundColor: '#718096'}}></div>
          </div>
          <div className="w-5/12 relative">
            <div className="absolute left-1/2 transform -translate-x-1/2" style={{top: '0', height: '2rem', width: '2px', backgroundColor: '#718096'}}></div>
          </div>
        </div>
      </div>
      
      {/* Treatment Options */}
      <div className="flex justify-between mb-8">
        <div className="bg-orange-400 text-white p-3 rounded text-center w-5/12">
          Lumpectomy
        </div>
        <div className="bg-orange-400 text-white p-3 rounded text-center w-5/12">
          Mastectomy
        </div>
      </div>
      
      {/* Connector from Lumpectomy to Radiation */}
      <div className="relative mb-4">
        <div className="absolute left-1/4 transform -translate-x-1/2" style={{top: '-2rem', height: '2rem', width: '2px', backgroundColor: '#718096'}}></div>
        <div className="bg-purple-500 text-white p-3 rounded text-center w-5/12">
          Radiation
        </div>
      </div>
      
      {/* Central connector to Hormone Therapy */}
      <div className="flex justify-center items-center mb-4">
        <div className="h-12 w-0.5 bg-gray-400"></div>
      </div>
      
      {/* Hormone Therapy Box */}
      <div className="bg-green-500 text-white p-3 rounded text-center w-1/2 mx-auto">
        Hormone Therapy
      </div>
    </div>
  );

  // Diagram mode content
  const DiagramContent = () => (
    <div className="relative p-4 border rounded-lg border-gray-300">
      <h3 className="text-center font-semibold mb-4">Treatment Flow Diagram</h3>
      
      <div className="flex justify-center mb-6">
        <div className="flex flex-col items-center">
          <div className="p-3 bg-blue-500 text-white rounded-full mb-2">Diagnosis</div>
          <div className="h-6 w-0.5 bg-gray-500"></div>
          <div className="p-3 bg-blue-500 text-white rounded-full my-2">Staging</div>
          <div className="h-6 w-0.5 bg-gray-500"></div>
        </div>
      </div>
      
      <div className="flex justify-center items-center mb-8">
        <div className="flex-1 border-t-2 border-gray-400"></div>
        <div className="mx-4 text-gray-500">Treatment Options</div>
        <div className="flex-1 border-t-2 border-gray-400"></div>
      </div>
      
      <div className="grid grid-cols-2 gap-8 mb-8">
        <div className="flex flex-col items-center">
          <div className="p-3 bg-orange-400 text-white rounded-lg mb-2 w-full text-center">Lumpectomy</div>
          <div className="h-6 w-0.5 bg-gray-500"></div>
          <div className="p-3 bg-purple-500 text-white rounded-lg w-full text-center">Radiation</div>
        </div>
        <div className="flex flex-col items-center">
          <div className="p-3 bg-orange-400 text-white rounded-lg mb-2 w-full text-center">Mastectomy</div>
        </div>
      </div>
      
      <div className="flex justify-center items-center mb-2">
        <div className="flex-1 border-t-2 border-gray-400"></div>
        <div className="mx-4 text-gray-500">Follow-up Therapy</div>
        <div className="flex-1 border-t-2 border-gray-400"></div>
      </div>
      
      <div className="flex justify-center">
        <div className="p-3 bg-green-500 text-white rounded-lg w-1/2 text-center">Hormone Therapy</div>
      </div>
    </div>
  );

  // Text-only mode content
  const TextOnlyContent = () => (
    <div className="p-4 border rounded-lg border-gray-300">
      <h3 className="font-semibold text-lg mb-4">Breast Cancer Treatment Process</h3>
      
      <ol className="list-decimal pl-6 space-y-4">
        <li className="mb-2">
          <p className="font-medium">Diagnosis</p>
          <p className="text-gray-600">Initial diagnosis through mammogram, ultrasound, and biopsy.</p>
        </li>
        <li className="mb-2">
          <p className="font-medium">Staging</p>
          <p className="text-gray-600">Determining the extent of the cancer (Stages 0-IV).</p>
        </li>
        <li className="mb-2">
          <p className="font-medium">Surgery Options</p>
          <ul className="list-disc pl-6 mt-2">
            <li><strong>Lumpectomy:</strong> Removal of the tumor and some surrounding tissue. Radiation typically follows.</li>
            <li><strong>Mastectomy:</strong> Removal of the entire breast.</li>
          </ul>
        </li>
        <li className="mb-2">
          <p className="font-medium">Radiation Therapy</p>
          <p className="text-gray-600">Usually follows lumpectomy to destroy any remaining cancer cells.</p>
        </li>
        <li className="mb-2">
          <p className="font-medium">Hormone Therapy</p>
          <p className="text-gray-600">For hormone receptor-positive cancers. Reduces risk of recurrence.</p>
        </li>
      </ol>
    </div>
  );

  // Interactive mode content
  const InteractiveContent = () => (
    <div className="p-4 border rounded-lg border-gray-300">
      <h3 className="font-semibold text-lg mb-4 text-center">Interactive Treatment Navigator</h3>
      
      <div className="mb-6">
        <div className="bg-blue-500 text-white p-3 rounded text-center mb-2 cursor-pointer hover:bg-blue-600 transition-colors"
          onClick={() => toast.info("Diagnosis details: Finding breast cancer through screening and diagnostic tests.")}>
          Diagnosis (Click for details)
        </div>
        
        <div className="bg-blue-500 text-white p-3 rounded text-center mb-4 cursor-pointer hover:bg-blue-600 transition-colors"
          onClick={() => toast.info("Staging details: Determines how far the cancer has spread and treatment approach.")}>
          Staging (Click for details)
        </div>
      </div>
      
      <div className="flex justify-between mb-6">
        <div className="bg-orange-400 text-white p-3 rounded text-center w-5/12 cursor-pointer hover:bg-orange-500 transition-colors"
          onClick={() => toast.info("Lumpectomy details: Breast-conserving surgery that removes only the tumor and some surrounding tissue.")}>
          Lumpectomy (Click)
        </div>
        <div className="bg-orange-400 text-white p-3 rounded text-center w-5/12 cursor-pointer hover:bg-orange-500 transition-colors"
          onClick={() => toast.info("Mastectomy details: Surgery that removes the entire breast to treat or prevent breast cancer.")}>
          Mastectomy (Click)
        </div>
      </div>
      
      <div className="mb-6">
        <div className="bg-purple-500 text-white p-3 rounded text-center w-5/12 cursor-pointer hover:bg-purple-600 transition-colors"
          onClick={() => toast.info("Radiation therapy details: Uses high-energy rays to kill cancer cells remaining after surgery.")}>
          Radiation (Click)
        </div>
      </div>
      
      <div className="mb-2">
        <div className="bg-green-500 text-white p-3 rounded text-center w-1/2 mx-auto cursor-pointer hover:bg-green-600 transition-colors"
          onClick={() => toast.info("Hormone therapy details: Blocks hormones that can stimulate cancer growth in hormone-receptor-positive cancers.")}>
          Hormone Therapy (Click)
        </div>
      </div>
      
      <p className="text-center text-sm text-gray-500 mt-4">Click each treatment option for more details</p>
    </div>
  );

  // Render content based on selected visual style
  const renderContent = () => {
    switch(visualStyle) {
      case 'Text-only':
        return <TextOnlyContent />;
      case 'Diagram':
        return <DiagramContent />;
      case 'Interactive':
        return <InteractiveContent />;
      case 'Illustration':
      default:
        return <IllustrationContent />;
    }
  };

  return (
    <MainLayout
      activeNavItem="education"
      searchPlaceholder="Patient: Sarah Smith"
      notifications={3}
    >
      <div className="p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">Breast Cancer Treatment Options</h2>
          <button 
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={handleCustomize}
          >
            Customize
          </button>
        </div>
       
        <div className="bg-white p-6 rounded-lg shadow mb-6">
          <div className="mb-6">
            <div className="w-full max-w-2xl mx-auto">
              {renderContent()}
            </div>
          </div>
         
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-2">Key Facts:</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>Surgery options: Lumpectomy vs Mastectomy</li>
              <li>Radiation typically follows lumpectomy</li>
              <li>Hormone therapy reduces recurrence risk</li>
              <li>Treatment typically spans 3-6 months</li>
            </ul>
          </div>
         
          <div>
            <h3 className="text-lg font-medium mb-2">Recommended Questions to Ask:</h3>
            <ul className="list-disc pl-6 space-y-1">
              <li>What are the side effects of each option?</li>
              <li>How will this affect my daily activities?</li>
              <li>What support resources are available?</li>
            </ul>
          </div>
        </div>
      </div>
     
      <div className="fixed top-24 right-6 w-72 bg-gray-100 p-4 rounded-lg shadow">
        <h3 className="text-lg font-medium mb-3">Settings:</h3>
       
        <div className="mb-4">
          <p className="text-sm mb-1">Health Literacy:</p>
          <input 
            type="range" 
            min="0" 
            max="100" 
            value={healthLiteracy} 
            onChange={(e) => setHealthLiteracy(parseInt(e.target.value))}
            className="w-full"
          />
          <div className="flex justify-between text-xs text-gray-600">
            <span>Basic</span>
            <span>Middle</span>
            <span>Advanced</span>
          </div>
        </div>
       
        <div className="mb-4">
          <p className="text-sm mb-1">Language:</p>
          <select 
            className="w-full p-2 border rounded"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
          >
            <option>English</option>
            <option>Spanish</option>
            <option>Chinese</option>
            <option>French</option>
            <option>Arabic</option>
          </select>
        </div>
       
        <div className="mb-4 relative">
          <p className="text-sm mb-1">Visual Style:</p>
          <div 
            className="w-full p-2 border rounded flex justify-between items-center cursor-pointer"
            onClick={() => setVisualStyleMenuOpen(!visualStyleMenuOpen)}
          >
            <span>{visualStyle}</span>
            <span>▼</span>
          </div>
          
          {visualStyleMenuOpen && (
            <div className="absolute z-10 w-full bg-white border rounded mt-1 shadow-lg">
              <div 
                className={`p-2 hover:bg-gray-100 cursor-pointer ${visualStyle === 'Illustration' ? 'bg-blue-100' : ''}`}
                onClick={() => handleVisualStyleChange('Illustration')}
              >
                Illustration
              </div>
              <div 
                className={`p-2 hover:bg-gray-100 cursor-pointer ${visualStyle === 'Diagram' ? 'bg-blue-100' : ''}`}
                onClick={() => handleVisualStyleChange('Diagram')}
              >
                Diagram
              </div>
              <div 
                className={`p-2 hover:bg-gray-100 cursor-pointer ${visualStyle === 'Text-only' ? 'bg-blue-100' : ''}`}
                onClick={() => handleVisualStyleChange('Text-only')}
              >
                Text-only
              </div>
              <div 
                className={`p-2 hover:bg-gray-100 cursor-pointer ${visualStyle === 'Interactive' ? 'bg-blue-100' : ''}`}
                onClick={() => handleVisualStyleChange('Interactive')}
              >
                Interactive
              </div>
            </div>
          )}
        </div>
       
        <div className="mb-4">
          <p className="text-sm mb-1">Resources to Include:</p>
          <div className="space-y-1">
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="side-effects" 
                className="mr-2" 
                checked={resources.sideEffects}
                onChange={() => handleResourceToggle('sideEffects')}
              />
              <label htmlFor="side-effects">Side effects</label>
            </div>
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="timeline" 
                className="mr-2" 
                checked={resources.timeline}
                onChange={() => handleResourceToggle('timeline')}
              />
              <label htmlFor="timeline">Timeline</label>
            </div>
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="statistics" 
                className="mr-2" 
                checked={resources.statistics}
                onChange={() => handleResourceToggle('statistics')}
              />
              <label htmlFor="statistics">Statistics</label>
            </div>
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="questions" 
                className="mr-2" 
                checked={resources.questions}
                onChange={() => handleResourceToggle('questions')}
              />
              <label htmlFor="questions">Questions</label>
            </div>
          </div>
        </div>
       
        <div className="space-y-2">
          <p className="text-sm mb-1">Share Options:</p>
          <button 
            className="w-full py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => handleShare('email')}
          >
            Email
          </button>
          <button 
            className="w-full py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => handleShare('print')}
          >
            Print
          </button>
          <button 
            className="w-full py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            onClick={() => handleShare('portal')}
          >
            Patient Portal
          </button>
        </div>
      </div>
    </MainLayout>
  );
};

export default EducationResources;